'use client'

import React, { createContext, useContext, useMemo } from 'react'
import { useRouter as useNextRouter, useSearchParams } from 'next/navigation'
import { useRouter as usePagesRouter } from 'next/router'
import type { Url } from 'next/dist/shared/lib/router/router'

interface RouterFacade {
  readonly query: Record<string, string | string[] | undefined>
  readonly push: (url: Url, as?: Url, options?: { shallow?: boolean }) => Promise<boolean> | void
}

interface NextLegacyFacadeContextValue {
  router: RouterFacade
}

const NextLegacyFacadeContext = createContext<NextLegacyFacadeContextValue | null>(null)

interface NextLegacyFacadeProviderProps {
  children: React.ReactNode
}

function AppRouterProvider({ children }: NextLegacyFacadeProviderProps) {
  const appRouter = useNextRouter()
  const searchParams = useSearchParams()

  const value = useMemo(() => {
    const query: Record<string, string | string[] | undefined> = {}

    if (searchParams) {
      searchParams.forEach((paramValue, key) => {
        const existing = query[key]
        if (existing === undefined) {
          query[key] = paramValue
        } else if (Array.isArray(existing)) {
          existing.push(paramValue)
        } else {
          query[key] = [existing, paramValue]
        }
      })
    }

    const router: RouterFacade = {
      query,
      push: (url: Url) => {
        if (typeof url === 'string') {
          appRouter.push(url)
        } else {
          const pathname = url.pathname || '/'
          const urlQuery = url.query || {}
          const queryString =
            Object.keys(urlQuery).length > 0
              ? `?${new URLSearchParams(urlQuery as Record<string, string>).toString()}`
              : ''
          appRouter.push(`${pathname}${queryString}`)
        }
      },
    }

    return { router }
  }, [appRouter, searchParams])

  return (
    <NextLegacyFacadeContext.Provider value={value}>{children}</NextLegacyFacadeContext.Provider>
  )
}

function PagesRouterProvider({ children }: NextLegacyFacadeProviderProps) {
  const pagesRouter = usePagesRouter()

  const value = useMemo(() => {
    const router: RouterFacade = {
      query: pagesRouter.query,
      push: pagesRouter.push,
    }

    return { router }
  }, [pagesRouter])

  return (
    <NextLegacyFacadeContext.Provider value={value}>{children}</NextLegacyFacadeContext.Provider>
  )
}

export function NextLegacyFacadeProvider({
  children,
  isAppRouter = false,
}: NextLegacyFacadeProviderProps & { isAppRouter?: boolean }) {
  if (isAppRouter) {
    return <AppRouterProvider>{children}</AppRouterProvider>
  }

  return <PagesRouterProvider>{children}</PagesRouterProvider>
}

export function useNextLegacyFacade() {
  const context = useContext(NextLegacyFacadeContext)
  if (!context) {
    throw new Error('useNextLegacyFacade must be used within NextLegacyFacadeProvider')
  }
  return context
}
